/**
 * Standards-based BCP 47 locale matching (REQ-01), with no dependency beyond `Intl`.
 *
 * The player's preferences are walked IN THEIR ORDER (RFC 4647 "lookup" semantics), and for
 * each one we try, in turn:
 *
 *   1. an exact match (case-insensitive, after `Intl.getCanonicalLocales`);
 *   2. a compatible variant: the same language AND script once both tags are expanded with
 *      CLDR likely subtags (`Intl.Locale#maximize`). A supported locale written without a region
 *      (`es`, `zh-Hans`) accepts every region; one written with a region (`pt-BR`) accepts only
 *      its own, so `pt-PT` does not slide into Brazilian Portuguese by accident;
 *   3. a configured cross-variant fallback (`LOCALE_VARIANT_FALLBACKS`, e.g. `pt-PT → pt-BR`).
 *
 * Only then does the next preference get a turn, so `["es-AR", "en"]` picks Spanish: the
 * player ranked it first. The script comparison is what keeps Traditional Chinese
 * (`zh-TW`, `zh-HK` → `Hant`) away from Simplified (`zh-Hans`).
 *
 * Nothing here looks at IP address or country. Language is not nationality.
 */
import { LOCALE_VARIANT_FALLBACKS } from "./localeConfig";

interface ExpandedTag {
  readonly canonical: string;
  readonly language: string;
  readonly script: string | undefined;
  readonly region: string | undefined;
  /** The region as WRITTEN, before likely-subtag expansion. */
  readonly explicitRegion: string | undefined;
}

function expand(tag: string): ExpandedTag | null {
  try {
    const [canonical] = Intl.getCanonicalLocales(tag);
    if (!canonical) return null;
    const written = new Intl.Locale(canonical);
    const max = written.maximize();
    return {
      canonical,
      language: max.language,
      script: max.script,
      region: max.region,
      explicitRegion: written.region,
    };
  } catch {
    // A malformed tag (`"en_US"`, `"*"`, `""`) is skipped, never thrown on.
    return null;
  }
}

function isCompatible(requested: ExpandedTag, supported: ExpandedTag): boolean {
  if (requested.language !== supported.language) return false;
  if (requested.script !== supported.script) return false;
  return supported.explicitRegion === undefined || supported.explicitRegion === requested.region;
}

/**
 * The best supported locale for an ordered list of preferences, or null when none matches
 * (the caller then falls back to English). `supported` is returned verbatim, so its spelling
 * is the catalog's.
 */
export function matchLocale<T extends string>(
  requested: readonly string[],
  supported: readonly T[],
  variantFallbacks: Readonly<Record<string, string>> = LOCALE_VARIANT_FALLBACKS,
): T | null {
  const candidates = supported
    .map((code) => ({ code, tag: expand(code) }))
    .filter((c): c is { code: T; tag: ExpandedTag } => c.tag !== null);

  for (const raw of requested) {
    const want = expand(raw);
    if (!want) continue;

    const exact = candidates.find((c) => c.tag.canonical.toLowerCase() === want.canonical.toLowerCase());
    if (exact) return exact.code;

    const compatible = candidates.find((c) => isCompatible(want, c.tag));
    if (compatible) return compatible.code;

    const fallbackKeys = [want.region ? `${want.language}-${want.region}` : undefined, want.language];
    for (const key of fallbackKeys) {
      if (!key) continue;
      const target = variantFallbacks[key];
      const hit = target ? candidates.find((c) => c.code.toLowerCase() === target.toLowerCase()) : undefined;
      if (hit) return hit.code;
    }
  }
  return null;
}

/** Highest q-value a tag may carry. */
const MAX_QUALITY = 1;

/**
 * The language tags of an HTTP `Accept-Language` header, best first. `q=0` entries and the
 * `*` wildcard are dropped; ties keep header order. Used by the server for the first response.
 */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(",")
    .map((part, index) => {
      const [tag = "", ...params] = part.trim().split(";");
      const qParam = params.map((p) => p.trim()).find((p) => p.startsWith("q="));
      const q = qParam ? Number(qParam.slice(2)) : MAX_QUALITY;
      return { tag: tag.trim(), q: Number.isFinite(q) ? q : 0, index };
    })
    .filter((e) => e.tag !== "" && e.tag !== "*" && e.q > 0)
    .sort((a, b) => b.q - a.q || a.index - b.index)
    .map((e) => e.tag);
}
